<template>
  <div class="file-uploader">
    <!-- 文件上传区域 -->
    <div 
      class="upload-area"
      @drop.prevent="handleFileDrop"
      @dragover.prevent
      @dragenter.prevent
    >
      <input 
        type="file" 
        id="file-input" 
        ref="fileInput" 
        multiple 
        @change="handleFileSelect" 
        style="display: none"
      >
      <div class="upload-content">
        <div class="upload-icon">
          <i class="upload-arrow"></i>
        </div>
        <p>Drag files here or <span @click="triggerFileInput" class="browse-link">browse</span></p>
      </div>
    </div>
    
    <!-- 上传进度 -->
    <div v-if="uploading" class="upload-progress">
      <div class="progress-bar">
        <div class="progress-value" :style="{ width: uploadProgress + '%' }"></div>
      </div>
      <span>{{ uploadProgress }}%</span>
    </div>
    
    <!-- 文件列表 -->
    <div v-if="files.length > 0" class="files-list">
      <div v-for="file in files" :key="file.unique_id" class="file-item">
        <div class="file-info">
          <div class="file-name-container">
            <span class="file-type-label" 
                  :style="{ backgroundColor: getFileTypeColor(getFileType(file)) }">
              {{ getFileTypeLabel(getFileType(file)) }}
            </span>
            <span class="file-name">{{ file.name || file.filename }}</span>
          </div>
          <div v-if="file.content" class="file-description">{{ file.content }}</div>
          <div class="file-meta">
            <div class="meta-row">
              <div class="meta-item">
                <Icon name="file-text" :size="12" />
                <span class="meta-text">{{ file.filename }}</span>
              </div>
              <div class="meta-separator">•</div>
              <div class="meta-item">
                <Icon name="hard-drive" :size="12" />
                <span class="meta-text">{{ formatFileSize(file.file_size) }}</span>
              </div>
              <div class="meta-separator">•</div>
              <div class="meta-item">
                <Icon name="clock" :size="12" />
                <span class="meta-text">{{ file.created_at }}</span>
              </div>
              <div class="meta-separator">•</div>
              <div class="meta-item">
                <Icon name="download" :size="12" />
                <span class="meta-text">{{ file.download_count }} downloads</span>
              </div>
              <div class="meta-separator">•</div>
              <div class="meta-item">
                <Icon name="hash" :size="12" />
                <span class="meta-text meta-id">{{ file.unique_id }}</span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 按钮区域移到右下角 -->
        <div class="file-actions">
          <button v-if="canPreview(file)" @click="previewFile(file)" class="action-btn preview-btn" title="Preview file">
            <Icon name="search" :size="16" />
          </button>
          <button @click="editFileInfo(file)" class="action-btn edit-btn" title="Edit file info">
            <Icon name="edit" :size="16" />
          </button>
          <button @click="copyFileUrl(file.unique_id)" class="action-btn copy-url-btn" title="Copy Download URL">
            <span v-if="!copiedUrls[file.unique_id]">
              <Icon name="copy" :size="16" />
            </span>
            <span v-else class="copy-success">
              <Icon name="check" :size="16" :stroke-width="3" />
            </span>
          </button>
          <button @click="downloadFile(file.unique_id)" class="action-btn download-btn" title="Download file">
            <Icon name="download" :size="16" />
          </button>
          <button @click="confirmDelete(file.unique_id)" class="action-btn delete-btn" title="Delete file">
            <Icon name="delete" :size="16" />
          </button>
        </div>
      </div>
    </div>
    <div v-else-if="!uploading" class="no-files">
      <p>No files uploaded yet</p>
    </div>

    <!-- File Preview Modal -->
    <div v-if="previewModal.visible" class="preview-modal-overlay" @click="closePreview">
      <div class="preview-modal-content" @click.stop>
        <div class="preview-modal-header">
          <h3>{{ previewModal.file?.name || previewModal.file?.filename }}</h3>
          <button @click="closePreview" class="preview-modal-close">
            <Icon name="close" :size="20" />
          </button>
        </div>
        <div class="preview-modal-body">
          <!-- Video Preview -->
          <video v-if="previewModal.type === 'video'" 
                 :src="buildFileUrl(previewModal.file?.unique_id, true)" 
                 controls 
                 class="preview-video">
            Your browser does not support the video tag.
          </video>
          
          <!-- Image Preview -->
          <img v-else-if="previewModal.type === 'image'" 
               :src="buildFileUrl(previewModal.file?.unique_id, true)" 
               :alt="previewModal.file?.filename"
               class="preview-image">
          
          <!-- PDF Preview -->
          <iframe v-else-if="previewModal.type === 'pdf'"
                  :src="buildFileUrl(previewModal.file?.unique_id, true)"
                  class="preview-pdf"
                  frameborder="0">
            Your browser does not support PDF preview.
          </iframe>
          
          <!-- Text Preview -->
          <div v-else-if="previewModal.type === 'text'" class="preview-text-container">
            <div v-if="previewModal.loading" class="preview-loading">
              Loading file content...
            </div>
            <Codemirror v-else
                        :value="previewModal.textContent" 
                        :options="textPreviewOptions"
                        :border="false"
                        height="100%"
                        :disabled="true" />
          </div>
        </div>
      </div>
    </div>

    <!-- File Edit Modal -->
    <div v-if="editModal.visible" class="edit-modal-overlay" @click="closeEditModal">
      <div class="edit-modal-content" @click.stop>
        <div class="edit-modal-header">
          <h3>Edit File Info</h3>
          <button @click="closeEditModal" class="edit-modal-close">
            <Icon name="close" :size="20" />
          </button>
        </div>
        <div class="edit-modal-body">
          <div class="edit-field">
            <label for="file-name-input">Display Name:</label>
            <input 
              id="file-name-input"
              v-model="editModal.name" 
              placeholder="Custom name for file (leave empty to use filename)"
              class="edit-input"
            />
          </div>
          <div class="edit-field">
            <label for="file-content-input">Description:</label>
            <textarea 
              id="file-content-input"
              v-model="editModal.content" 
              placeholder="Description of file content"
              class="edit-textarea"
              rows="4"
            ></textarea>
          </div>
        </div>
        <div class="edit-modal-footer">
          <button @click="saveFileInfo" class="save-file-btn">Save</button>
          <button @click="closeEditModal" class="cancel-file-btn">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fileService } from '@/services/api';
import { buildFileUrl } from '@/config';
import Icon from './Icon.vue';
import Codemirror from "codemirror-editor-vue3";

export default {
  name: 'FileUploader',
  components: {
    Icon,
    Codemirror
  },
  props: {
    clipboardId: {
      type: String,
      required: true
    }
  },
  emits: ['file-count-changed'],
  data() {
    return {
      files: [],
      uploading: false,
      uploadProgress: 0,
      copiedUrls: {},
      previewModal: {
        visible: false,
        type: null, // 'video', 'image', 'pdf', 'text'
        file: null,
        textContent: '',
        loading: false
      },
      editModal: {
        visible: false,
        file: null,
        name: '',
        content: ''
      },
      textPreviewOptions: {
        mode: 'null',
        lineNumbers: true,
        lineWrapping: true,
        theme: 'default',
        tabSize: 4,
        readOnly: true,
        styleActiveLine: false,
        cursorBlinkRate: -1
      }
    };
  },
  mounted() {
    this.fetchFiles();
  },
  methods: {
    // 将导入的函数暴露给模板
    buildFileUrl,
    
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFileSelect(event) {
      const files = event.target.files;
      if (files.length) {
        this.uploadFiles(Array.from(files));
      }
    },
    handleFileDrop(event) {
      const files = event.dataTransfer.files;
      if (files.length) {
        this.uploadFiles(Array.from(files));
      }
    },
    async uploadFiles(files) {
      this.uploading = true;
      this.uploadProgress = 0;
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);
        
        try {
          await fileService.uploadFiles(
            this.clipboardId,
            formData,
            (progressEvent) => {
              // Calculate and update upload progress
              this.uploadProgress = Math.round(
                (progressEvent.loaded / progressEvent.total) * 100
              );
            }
          );
        } catch (error) {
          console.error('Error uploading file:', error);
          alert('Failed to upload file. Please try again.');
        }
      }
      
      // Update file list
      await this.fetchFiles();
      this.uploading = false;
    },
    async fetchFiles() {
      try {
        const response = await fileService.getFiles(this.clipboardId);
        this.files = response.data;
        this.$emit('file-count-changed', this.files.length);
      } catch (error) {
        console.error('Error fetching files:', error);
      }
    },
    downloadFile(fileUniqueId) {
      window.open(buildFileUrl(fileUniqueId, false), '_blank'); // 下载链接
    },
    async deleteFile(fileUniqueId) {
      try {
        await fileService.deleteFile(fileUniqueId);
        
        // Remove file from list
        this.files = this.files.filter(file => file.unique_id !== fileUniqueId);
        this.$emit('file-count-changed', this.files.length);
      } catch (error) {
        console.error('Error deleting file:', error);
        alert('Failed to delete file. Please try again.');
      }
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 B';
      
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    copyFileUrl(fileUniqueId) {
      const url = buildFileUrl(fileUniqueId, false); // 复制下载链接
      
      navigator.clipboard.writeText(url)
        .then(() => {
          this.copiedUrls = {
            ...this.copiedUrls,
            [fileUniqueId]: true
          };
          
          setTimeout(() => {
            this.copiedUrls = {
              ...this.copiedUrls,
              [fileUniqueId]: false
            };
          }, 2000);
        })
        .catch(err => {
          console.error('Failed to copy URL', err);
          alert('Failed to copy URL. Please try again.');
        });
    },

    confirmDelete(fileUniqueId) {
      if (confirm('Are you sure you want to delete this file?')) {
        this.deleteFile(fileUniqueId);
      }
    },

    canPreview(file) {
      const mimeType = file.mime_type.toLowerCase();
      
      // Video files
      if (mimeType.startsWith('video/')) {
        return true;
      }
      
      // Image files
      if (mimeType.startsWith('image/')) {
        return true;
      }
      
      // PDF files
      if (mimeType === 'application/pdf') {
        return true;
      }
      
      // Text files
      if (mimeType.startsWith('text/') || 
          mimeType.includes('json') ||
          mimeType.includes('xml') ||
          mimeType.includes('javascript') ||
          mimeType.includes('css') ||
          mimeType.includes('html')) {
        return true;
      }
      
      return false;
    },

    getFileType(file) {
      const mimeType = file.mime_type.toLowerCase();
      
      if (mimeType.startsWith('video/')) {
        return 'video';
      }
      
      if (mimeType.startsWith('image/')) {
        return 'image';
      }
      
      if (mimeType === 'application/pdf') {
        return 'pdf';
      }
      
      if (mimeType.startsWith('text/') || 
          mimeType.includes('json') ||
          mimeType.includes('xml') ||
          mimeType.includes('javascript') ||
          mimeType.includes('css') ||
          mimeType.includes('html')) {
        return 'text';
      }
      
      return 'unknown';
    },

    async previewFile(file) {
      const fileType = this.getFileType(file);
      
      this.previewModal = {
        visible: true,
        type: fileType,
        file: file,
        textContent: '',
        loading: false
      };
      
      // For text files, fetch the content
      if (fileType === 'text') {
        this.previewModal.loading = true;
        try {
          const response = await fileService.getFileContent(file.unique_id);
          this.previewModal.textContent = response.data;
        } catch (error) {
          console.error('Error fetching text content:', error);
          this.previewModal.textContent = 'Error loading file content.';
        } finally {
          this.previewModal.loading = false;
        }
      }
      
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';
    },

    closePreview() {
      this.previewModal.visible = false;
      this.previewModal.file = null;
      this.previewModal.textContent = '';
      // Restore body scrolling
      document.body.style.overflow = '';
    },

    editFileInfo(file) {
      this.editModal.file = file;
      this.editModal.name = file.name || file.filename; // Use filename as default if name is empty
      this.editModal.content = file.content || '';
      this.editModal.visible = true;
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';
    },

    closeEditModal() {
      this.editModal.visible = false;
      this.editModal.file = null;
      this.editModal.name = '';
      this.editModal.content = '';
      // Restore body scrolling
      document.body.style.overflow = '';
    },

    async saveFileInfo() {
      if (!this.editModal.file) return;

      try {
        const response = await fileService.updateFileInfo(
          this.editModal.file.unique_id,
          this.editModal.name.trim(),
          this.editModal.content.trim()
        );

        // Update the file in the list
        const index = this.files.findIndex(f => f.unique_id === this.editModal.file.unique_id);
        if (index !== -1) {
          this.files[index] = response.data;
        }

        this.closeEditModal();
      } catch (err) {
        console.error('Error updating file info', err);
        alert('Failed to update file info. Please try again.');
      }
    },

    getFileTypeLabel(fileType) {
      const labels = {
        'video': 'VIDEO',
        'image': 'IMAGE',
        'pdf': 'PDF',
        'text': 'TEXT',
        'unknown': 'FILE'
      };
      return labels[fileType] || 'FILE';
    },

    getFileTypeColor(fileType) {
      const colors = {
        'video': '#e74c3c',     // Red for video
        'image': '#9b59b6',     // Purple for images
        'pdf': '#e67e22',       // Orange for PDF
        'text': '#3498db',      // Blue for text
        'unknown': '#95a5a6'    // Gray for unknown
      };
      return colors[fileType] || '#95a5a6';
    }
  }
}
</script>

<style scoped>
.file-uploader {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.upload-area {
  border: 2px dashed #ddd;
  border-radius: 8px;
  padding: 25px;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.3s;
  margin-bottom: 20px;
}

.upload-area:hover {
  background-color: #f9f9f9;
  border-color: #ccc;
}

.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.upload-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #f1f1f1;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
}

.upload-arrow {
  border: solid #666;
  border-width: 0 2px 2px 0;
  padding: 5px;
  transform: rotate(-135deg);
  margin-bottom: 5px;
}

.browse-link {
  color: #42b983;
  font-weight: bold;
  cursor: pointer;
}

.upload-progress {
  margin: 20px 0;
}

.progress-bar {
  height: 8px;
  background-color: #f1f1f1;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 5px;
}

.progress-value {
  height: 100%;
  background-color: #42b983;
  transition: width 0.3s;
}

.files-list {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
}

.file-item {
  display: flex;
  flex-direction: column;
  border: 1px solid #e8f5e8;
  border-radius: 5px;
  padding: 15px;
  background-color: #f9f9f9;
  position: relative;
  width: 100%;
  transition: all 0.3s ease;
}

.file-item:hover {
  border-color: #42b983;
  box-shadow: 0 2px 8px rgba(66, 185, 131, 0.15);
  transform: translateY(-1px);
}

.file-info {
  flex-grow: 1;
  margin-bottom: 15px;
}

.file-name-container {
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.file-type-label {
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
}

.file-name {
  font-weight: bold;
  word-break: break-all;
  flex: 1;
}

.file-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 10px;
}

.meta-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.meta-text {
  color: #666;
}

.meta-id {
  font-family: monospace;
  font-size: 0.75rem;
  word-break: break-all;
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.meta-separator {
  color: #ccc;
  font-weight: bold;
  margin: 0 2px;
}

.meta-item svg {
  color: #42b983;
  flex-shrink: 0;
}

/* Responsive design for mobile */
@media (max-width: 768px) {
  .meta-row {
    gap: 4px;
  }
  
  .meta-separator {
    margin: 0 1px;
  }
  
  .meta-text {
    font-size: 0.75rem;
  }
  
  .meta-id {
    max-width: 120px;
  }
}

@media (max-width: 480px) {
  .meta-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }
  
  .meta-separator {
    display: none;
  }
  
  .meta-item {
    width: 100%;
  }
  
  .meta-id {
    max-width: none;
    font-size: 0.7rem;
  }
}

.file-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 5px;
}

.action-btn {
  padding: 5px 8px;
  border: none;
  border-radius: 3px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
}

.copy-url-btn {
  background-color: #7e57c2;
  color: white;
}

.copy-url-btn:hover {
  background-color: #673ab7;
}

.preview-btn {
  background-color: #17a2b8;
  color: white;
}

.preview-btn:hover {
  background-color: #138496;
}

.download-btn {
  background-color: #42b983;
  color: white;
}

.download-btn:hover {
  background-color: #36996b;
}

.delete-btn {
  background-color: #e74c3c;
  color: white;
}

.delete-btn:hover {
  background-color: #c0392b;
}

.copy-success {
  color: white;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 4px;
}

.checkmark {
  stroke: white;
  animation: checkmark 0.3s ease-in-out forwards;
}

@keyframes checkmark {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.no-files {
  text-align: center;
  padding: 20px;
  color: #666;
  background-color: #f9f9f9;
  border-radius: 5px;
  border: 1px dashed #ddd;
}

h2 {
  margin-top: 0;
  margin-bottom: 20px;
  color: #2c3e50;
}

.download-count {
  color: #666;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  margin-left: 15px;
}

.download-count::before {
  content: '↓';
  margin-right: 3px;
  font-weight: bold;
}

/* Preview Modal Styles */
.preview-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.preview-modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  height: 80%;
  max-width: 1200px;
  max-height: 800px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.preview-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e9ecef;
  background-color: #f8f9fa;
  border-radius: 8px 8px 0 0;
}

.preview-modal-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 18px;
  word-break: break-all;
}

.preview-modal-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: #6c757d;
  transition: all 0.2s ease;
}

.preview-modal-close:hover {
  background-color: #e9ecef;
  color: #495057;
}

.preview-modal-body {
  flex: 1;
  padding: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-video {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
}

.preview-pdf {
  width: 100%;
  height: 100%;
  border: none;
  background-color: #f8f9fa;
}

.preview-text-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.preview-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 16px;
  color: #6c757d;
}

.preview-text-container :deep(.CodeMirror) {
  height: 100% !important;
  border: none;
  font-size: 14px;
}

.preview-text-container :deep(.CodeMirror-scroll) {
  height: 100%;
}

/* File Description Styles */
.file-description {
  margin-top: 6px;
  font-size: 12px;
  color: #666;
  line-height: 1.4;
  font-style: italic;
}

/* Edit Modal Styles */
.edit-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.edit-modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.edit-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e9ecef;
  background-color: #f8f9fa;
  border-radius: 8px 8px 0 0;
}

.edit-modal-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 18px;
}

.edit-modal-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: #6c757d;
  transition: all 0.2s ease;
}

.edit-modal-close:hover {
  background-color: #e9ecef;
  color: #495057;
}

.edit-modal-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.edit-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.edit-field label {
  font-weight: 500;
  color: #2c3e50;
  font-size: 14px;
}

.edit-input, .edit-textarea {
  padding: 10px 12px;
  border: 2px solid #e9ecef;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  font-family: inherit;
}

.edit-input:focus, .edit-textarea:focus {
  outline: none;
  border-color: #42b983;
  box-shadow: 0 0 0 3px rgba(66, 185, 131, 0.1);
}

.edit-textarea {
  resize: vertical;
  min-height: 80px;
}

.edit-modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #e9ecef;
  background-color: #f8f9fa;
  border-radius: 0 0 8px 8px;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.save-file-btn, .cancel-file-btn {
  padding: 8px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.save-file-btn {
  background-color: #42b983;
  color: white;
}

.save-file-btn:hover {
  background-color: #36996b;
}

.cancel-file-btn {
  background-color: #6c757d;
  color: white;
}

.cancel-file-btn:hover {
  background-color: #5a6268;
}

.edit-btn {
  background-color: #3498db;
  color: white;
}

.edit-btn:hover {
  background-color: #2980b9;
  transform: translateY(-1px);
}
</style> 